import { useEffect, useState, useMemo } from 'react';
import styles from './style.module.scss';
import { List, Divider, Row, Col, Spin, Select, message, Typography } from 'antd';
import { Button, PageHeader } from 'components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UIHelper from 'helpers/UIHelper';
import useStoreEvents from '../useStoreEvents';
import FiltersApi from 'libs/api/filters';
import { getStoreEventViewSections } from '../storeEventViewSections';

const apiFilters = new FiltersApi();

const ViewSection = ({ title, fields }) => (
  <List
    header={<div>{title}</div>}
    style={{ marginTop: 24 }}
    dataSource={fields}
    renderItem={(item) => (
      <List.Item>
        <b className={styles.itemName}>{item.text}</b>
        {item.value}
      </List.Item>
    )}
  />
);

const SingleStoreEvent = () => {
  const { t } = useTranslation();
  const {
    singleItem,
    loading,
    handleMemberApprove,
    handleCheckMemberByCompany,
    shoppingCenters,
    companyCategories,
    isShoppingCenter,
  } = useStoreEvents(false);

  const companyCategoriesIds = useMemo(
    () => singleItem?.allData?.company_categories?.map((category) => category.id) || [],
    [singleItem],
  );

  const [allCompaniesByCompanyCategory, setAllCompaniesByCompanyCategory] =
    useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (companyCategoriesIds.length > 0) {
          const { data } = await apiFilters.list({
            params: {
              by_company_categories: companyCategoriesIds,
            },
          });
          const list = data.all_companies_by_company_category || [];
          if (!cancelled) setAllCompaniesByCompanyCategory(list);
        }
      } catch (error) {
        if (!cancelled) console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyCategoriesIds]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [disableApproveRejectBt, setDisableApproveRejectBt] = useState(false);
  const [commentTextBefore, setCommentTextBefore] = useState('');

  const sections = useMemo(
    () =>
      getStoreEventViewSections(t, singleItem, {
        isShoppingCenter,
        shoppingCenters,
        companyCategories,
      }),
    [t, singleItem, isShoppingCenter, shoppingCenters, companyCategories],
  );

  const validateForm = () => {
    if (!selectedCompany) {
      message.error(t("L'insegna deve essere selezionata"));
      return false;
    }
    return true;
  };

  const handleApprove = async () => {
    if (!validateForm()) return;
    await handleMemberApprove({
      company_id: selectedCompany,
      comment: '',
      confirmed: true,
    });
    setSelectedCompany(null);
  };

  const handleReject = async () => {
    if (!validateForm()) return;
    await handleMemberApprove({
      company_id: selectedCompany,
      comment: '',
      confirmed: false,
    });
    setSelectedCompany(null);
  };

  useEffect(() => {
    if (!selectedCompany) return;
    handleCheckMemberByCompany(selectedCompany)
      .then((response) => {
        if (!response?.member) return;
        setCommentTextBefore(response.member.comment ?? '');
        UIHelper.openNotificationWithIcon(
          'error',
          response.member.confirmed
            ? t('Evento già approvato')
            : t('Evento non approvato'),
        );
        setDisableApproveRejectBt(true);
      })
      .catch(() => {
        setCommentTextBefore('');
        setDisableApproveRejectBt(false);
      });
  }, [selectedCompany, handleCheckMemberByCompany, t]);

  const pageTitle = singleItem?.title_it || singleItem?.title || t('EVENTO');

  return (
    <div>
      <PageHeader title={pageTitle} />
      <div className={styles.content}>
        <Spin spinning={loading}>
          {sections.map((section) => (
            <ViewSection
              key={section.title}
              title={section.title}
              fields={section.fields}
            />
          ))}
        </Spin>

        {companyCategoriesIds.length > 0 &&
          allCompaniesByCompanyCategory.length > 0 && (
            <div className={styles.approveForm}>
              <Divider />
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Select
                    placeholder={t('Insegna')}
                    style={{ width: '100%' }}
                    value={selectedCompany}
                    onSelect={(value) => {
                      setSelectedCompany(value);
                      setDisableApproveRejectBt(false);
                      setCommentTextBefore('');
                    }}
                  >
                    <Select.Option value={''}>
                      {t('Selezioni opzione')}
                    </Select.Option>
                    {allCompaniesByCompanyCategory.map((company) => (
                      <Select.Option key={company.value} value={company.value}>
                        {company.title}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                {commentTextBefore && (
                  <Col span={24}>
                    <Typography.Text strong>{t('Commento')}: </Typography.Text>
                    <Typography.Text>{commentTextBefore}</Typography.Text>
                  </Col>
                )}
                <Col span={24}>
                  <Row gutter={[16, 16]}>
                    <Col>
                      <Button
                        type='primary'
                        disabled={disableApproveRejectBt}
                        onClick={handleApprove}
                      >
                        {t('Partecipo')}
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type='primary'
                        danger
                        disabled={disableApproveRejectBt}
                        onClick={handleReject}
                      >
                        {t('Non partecipo')}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          )}

        <Divider />
        <Row gutter={16}>
          {(singleItem?.can_update || singleItem?.can_approve) && (
            <Col>
              <Button>
                <Link to={`/promo/store_events/${singleItem?.id}/edit`}>
                  {t('MODIFICA')}
                </Link>
              </Button>
            </Col>
          )}
          <Col>
            <Button type={'secondary'}>
              <Link to={'/promo/store_events'}>{t('TORNA INDIETRO')}</Link>
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SingleStoreEvent;
